import type { LocalDateKey, RecurrenceRule, Weekday } from '@/types';
import {
  addDaysToDateKey,
  compareDateKeys,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  enumerateDateKeys,
  parseLocalDateKey,
  weekdayForDateKey,
  daysInMonth
} from './dates';

function isWithinBounds(rule: RecurrenceRule, date: LocalDateKey): boolean {
  if (rule.paused || (rule.endDate && compareDateKeys(date, rule.endDate) > 0)) return false;
  const start = rule.kind === 'once' ? rule.date : rule.startDate;
  return compareDateKeys(date, start) >= 0;
}

function isWeekdayMatch(date: LocalDateKey, weekdays: readonly Weekday[]): boolean {
  return weekdays.includes(weekdayForDateKey(date));
}

function matchesMonthlyDay(
  date: LocalDateKey,
  requestedDay: number,
  overflow: 'skip' | 'last-day'
): boolean {
  const { year, month, day } = parseLocalDateKey(date);
  const maximum = daysInMonth(year, month);
  if (requestedDay <= maximum) return day === requestedDay;
  return overflow === 'last-day' && day === maximum;
}

export function occursOnDate(rule: RecurrenceRule, date: LocalDateKey): boolean {
  if (!isWithinBounds(rule, date)) return false;

  switch (rule.kind) {
    case 'once':
      return date === rule.date;
    case 'daily': {
      if (!Number.isInteger(rule.intervalDays) || rule.intervalDays < 1) return false;
      return differenceInCalendarDays(date, rule.startDate) % rule.intervalDays === 0;
    }
    case 'weekdays': {
      if (!Number.isInteger(rule.intervalWeeks) || rule.intervalWeeks < 1) return false;
      const elapsedDays = differenceInCalendarDays(date, rule.startDate);
      return (
        Math.floor(elapsedDays / 7) % rule.intervalWeeks === 0 &&
        isWeekdayMatch(date, rule.weekdays)
      );
    }
    case 'weekly': {
      if (!Number.isInteger(rule.intervalWeeks) || rule.intervalWeeks < 1) return false;
      const elapsedDays = differenceInCalendarDays(date, rule.startDate);
      return (
        Math.floor(elapsedDays / 7) % rule.intervalWeeks === 0 &&
        weekdayForDateKey(date) === rule.weekday
      );
    }
    case 'monthly': {
      if (!Number.isInteger(rule.intervalMonths) || rule.intervalMonths < 1) return false;
      const elapsedMonths = differenceInCalendarMonths(date, rule.startDate);
      return (
        elapsedMonths >= 0 &&
        elapsedMonths % rule.intervalMonths === 0 &&
        matchesMonthlyDay(date, rule.dayOfMonth, rule.overflow)
      );
    }
    case 'custom': {
      if (!Number.isInteger(rule.interval) || rule.interval < 1) return false;
      if (rule.unit === 'days') {
        return differenceInCalendarDays(date, rule.startDate) % rule.interval === 0;
      }
      if (rule.unit === 'weeks') {
        const weekdays = rule.weekdays?.length
          ? rule.weekdays
          : [weekdayForDateKey(rule.startDate)];
        const elapsedDays = differenceInCalendarDays(date, rule.startDate);
        return Math.floor(elapsedDays / 7) % rule.interval === 0 && isWeekdayMatch(date, weekdays);
      }
      const elapsedMonths = differenceInCalendarMonths(date, rule.startDate);
      const targetDay = rule.dayOfMonth ?? parseLocalDateKey(rule.startDate).day;
      return (
        elapsedMonths >= 0 &&
        elapsedMonths % rule.interval === 0 &&
        matchesMonthlyDay(date, targetDay, rule.overflow ?? 'last-day')
      );
    }
  }
}

export function recurrenceDatesBetween(
  rule: RecurrenceRule,
  start: LocalDateKey,
  end: LocalDateKey
): LocalDateKey[] {
  return enumerateDateKeys(start, end).filter((date) => occursOnDate(rule, date));
}

export function nextRecurrenceDate(
  rule: RecurrenceRule,
  after: LocalDateKey,
  maximumLookAheadDays = 3660
): LocalDateKey | undefined {
  if (!Number.isInteger(maximumLookAheadDays) || maximumLookAheadDays < 1) {
    throw new RangeError('maximumLookAheadDays must be a positive integer');
  }
  for (let offset = 1; offset <= maximumLookAheadDays; offset += 1) {
    const candidate = addDaysToDateKey(after, offset);
    if (rule.endDate && compareDateKeys(candidate, rule.endDate) > 0) return undefined;
    if (occursOnDate(rule, candidate)) return candidate;
  }
  return undefined;
}
